import { isPreview, setPreviewMode } from "./preview";
import type { SessionConfig } from "@carbon-os/server/contract";

/**
 * Keeps the Clerk session token fresh while the page stays open.
 *
 * The server reads the person from Clerk's `__session` cookie, a token that
 * lives 60 seconds. clerkMiddleware renews it only on a document load, so
 * without this a tick, a theme save or a quiz answer made a minute after the
 * page opened would come back 401. Clerk's own browser script renews the
 * cookie in the background; it is loaded from the instance's Frontend API,
 * the way Clerk's JavaScript quickstart does.
 */

/** The slice of `window.Clerk` used here. */
interface ClerkGlobal {
  load(): Promise<void>;
  signOut(options: { redirectUrl: string }): Promise<void>;
  session?: { getToken(options?: { skipCache?: boolean }): Promise<string | null> } | null;
}

declare global {
  interface Window {
    Clerk?: ClerkGlobal;
  }
}

/** Clerk's script major version, as Clerk's own loaders name it. */
const CLERK_JS_MAJOR = 6;

/** How long a refused request waits for Clerk before giving up and handing back the 401. */
const REFRESH_TIMEOUT_MS = 5000;

/**
 * The Frontend API host a publishable key names: `pk_test_` or `pk_live_`,
 * then the host and a `$`, base64-encoded. Null for anything else.
 */
export function frontendApiOf(publishableKey: string): string | null {
  const match = /^pk_(test|live)_([A-Za-z0-9+/=_-]+)$/.exec(publishableKey);
  if (!match) return null;
  let decoded: string;
  try {
    decoded = atob(match[2]!.replace(/-/g, "+").replace(/_/g, "/"));
  } catch {
    return null;
  }
  const host = decoded.endsWith("$") ? decoded.slice(0, -1) : "";
  // A bare host name, so it can only ever become an https origin.
  return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(host) ? host : null;
}

let key: string | null = null;
// Settles true once Clerk has loaded, false when its script could not; a
// failed load is forgotten, so the next refusal tries again.
let loaded: Promise<boolean> | null = null;
let refreshing: Promise<boolean> | null = null;
// Module scope, not component state: StrictMode runs effects twice.
let starting: Promise<void> | null = null;

/** Loads Clerk's script once per page. Does nothing without a usable key, and never throws. */
export function keepSessionFresh(publishableKey: string | null, doc: Document = document): void {
  if (loaded || !publishableKey) return;
  const host = frontendApiOf(publishableKey);
  if (!host) return;
  key = publishableKey;
  loaded = new Promise<boolean>((resolve) => {
    const script = doc.createElement("script");
    const failed = () => {
      script.remove();
      loaded = null;
      resolve(false);
    };
    script.src = `https://${host}/npm/@clerk/clerk-js@${CLERK_JS_MAJOR}/dist/clerk.browser.js`;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.dataset["clerkPublishableKey"] = publishableKey;
    script.addEventListener("load", () => {
      if (!window.Clerk) return failed();
      window.Clerk.load().then(() => resolve(true), failed);
    });
    script.addEventListener("error", failed);
    doc.head.appendChild(script);
  });
}

/**
 * Asks the server for the key and starts the renewal. The key does not come
 * with the tool list, so a Gatekeeper outage at page load cannot switch it off.
 */
export function startSession(fetchImpl: typeof fetch = fetch): Promise<void> {
  starting ??= (async () => {
    try {
      const res = await fetchImpl("/api/session", { credentials: "same-origin" });
      if (!res.ok) return;
      const body = (await res.json()) as SessionConfig;
      keepSessionFresh(typeof body.clerkPublishableKey === "string" ? body.clerkPublishableKey : null);
    } catch {
      // Without it the page still works until the token expires.
    }
  })();
  return starting;
}

const timeout = (ms: number) => new Promise<false>((resolve) => setTimeout(() => resolve(false), ms));

/** Null means we cannot confirm session state; an expired server cookie is not proof of sign-out. */
export async function browserSessionState(): Promise<boolean | null> {
  const ready = (async () => {
    await startSession();
    return loaded ? await loaded : false;
  })();
  if (!(await Promise.race([ready, timeout(REFRESH_TIMEOUT_MS)])) || !window.Clerk) return null;
  return Boolean(window.Clerk.session);
}

/** Let Clerk clear its browser state and cookies before leaving the page. */
export async function endSession(): Promise<boolean> {
  try {
    if (!loaded || !(await Promise.race([loaded, timeout(REFRESH_TIMEOUT_MS)]))) return false;
    if (!window.Clerk) return false;
    await window.Clerk.signOut({ redirectUrl: "/signed-out" });
    return true;
  } catch {
    return false;
  }
}

async function mintToken(): Promise<boolean> {
  // A script that failed to load gets one more try here.
  if (!loaded) keepSessionFresh(key);
  if (!loaded || !(await loaded)) return false;
  return Boolean(await window.Clerk?.session?.getToken({ skipCache: true }));
}

/**
 * Asks Clerk for a new token now, which rewrites the cookie. True when one came
 * back, so the request that was refused can be sent again. Requests refused at
 * the same moment share one refresh, and none waits longer than five seconds.
 */
export function refreshSession(): Promise<boolean> {
  refreshing ??= Promise.race([mintToken().catch(() => false), timeout(REFRESH_TIMEOUT_MS)]).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/**
 * `fetch` for carbon-os's own API. A 401 is sent again once, after a token
 * refresh: the background renewal can lag behind a laptop waking up or a tab
 * coming back from the background. A request that never arrived is not
 * retried, and neither is a `Request`, whose body the first send used up.
 */
export async function sessionFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const path = typeof input === "string" ? input : input instanceof URL ? input.pathname : input.url;
  const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
  if (isPreview() && method !== "GET" && method !== "HEAD" && !path.startsWith("/api/admin/")) {
    return Response.json({ code: "preview_read_only", error: "Viewing as another user is read-only." }, { status: 403 });
  }
  const res = await fetch(input, init);
  if (res.headers.get("X-CarbonOS-Preview") === "1") setPreviewMode(true);
  if (res.status !== 401 || input instanceof Request || !(await refreshSession())) return res;
  await res.body?.cancel().catch(() => {});
  return fetch(input, init);
}
